import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Modal, Alert, Form, Card, Table } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { PlusOutlined, EditOutlined, DeleteOutlined, FilterOutlined } from '@ant-design/icons';
import { Select } from 'antd';
import PageTitle from '@/common/PageTitle/PageTitle';
import FormItem from '@/common/FormItem/FormItem';
import DateRangePicker from '@/common/DateRangePicker/DateRangePicker';
import dayjs from 'dayjs';
import styles from './inventory-product-inventory.module.scss';

// Mock device data for individual KrossKast Players (only available/unassigned devices)
const deviceData = {
  '1': [ // KrossKast Player 8 devices - only available inventory
    {
      id: 'device-1-3',
      serialNumber: 'KKP8-003-GHI789',
      customId: '',
      status: 'Available',
      location: 'Warehouse',
      lastSeen: ''
    },
    {
      id: 'device-1-4',
      serialNumber: 'KKP8-004-JKL012',
      customId: '',
      status: 'Available',
      location: 'Warehouse',
      lastSeen: ''
    },
    {
      id: 'device-1-5',
      serialNumber: 'KKP8-005-MNO345',
      customId: '',
      status: 'Available',
      location: 'Warehouse',
      lastSeen: ''
    }
  ],
  '2': [ // KrossKast Player 11 devices - only available inventory
    {
      id: 'device-2-2',
      serialNumber: 'KKP11-002-MNO345',
      customId: '',
      status: 'Available',
      location: 'Warehouse',
      lastSeen: ''
    },
    {
      id: 'device-2-3',
      serialNumber: 'KKP11-003-PQR678',
      customId: '',
      status: 'Available',
      location: 'Warehouse',
      lastSeen: ''
    }
  ]
};

// Product data for titles
const productsData = {
  '1': {
    id: '1',
    name: 'KrossKast Player 8',
    brand: 'KrossKast',
    category: 'KK Players',
  },
  '2': {
    id: '2',
    name: 'KrossKast Player 11',
    brand: 'KrossKast',
    category: 'KK Players',
  },
};

const ProductInventory: React.FC = () => {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const product = productsData[productId as keyof typeof productsData];
  
  // Filter state - matching Device Assignments page
  const [filters, setFilters] = useState<{
    serialNumber: string | null;
    status: string | null;
    location: string | null;
    playTimeStart: string | null;
    playTimeEnd: string | null;
  }>({
    serialNumber: null,
    status: null,
    location: null,
    playTimeStart: null,
    playTimeEnd: null
  });
  
  // State for device management
  const [devices, setDevices] = useState(deviceData[productId as keyof typeof deviceData] || []);
  const [showAddDeviceModal, setShowAddDeviceModal] = useState(false);
  const [showEditDeviceModal, setShowEditDeviceModal] = useState(false);
  const [editingDevice, setEditingDevice] = useState<any>(null);
  const [showSuccessAlert, setShowSuccessAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [resetCounter, setResetCounter] = useState(0);

  // Filter options - matching Device Assignments page pattern
  const filterOptions = {
    statuses: [
      { value: 'available', label: 'Available' },
      { value: 'assigned', label: 'Assigned' },
      { value: 'maintenance', label: 'Maintenance' }
    ],
    locations: [
      { value: 'warehouse', label: 'Warehouse' },
      { value: 'office', label: 'Office' },
      { value: 'shipping', label: 'Shipping' }
    ]
  };

  const selectInputs = [
    {
      label: "Serial Number",
      name: "serialNumber",
      placeholder: "Search by serial number",
      options: devices.map(device => ({ value: device.serialNumber, label: device.serialNumber }))
    },
    { 
      label: "Status", 
      name: "status", 
      placeholder: "Any status",
      options: filterOptions.statuses
    },
    { 
      label: "Location", 
      name: "location", 
      placeholder: "Any location",
      options: filterOptions.locations
    },
  ];

  // Filtering logic
  const filteredDevices = devices.filter(device => {
    const matchesSerialNumber = !filters.serialNumber || device.serialNumber.toLowerCase().includes(filters.serialNumber.toLowerCase());
    const matchesStatus = !filters.status || device.status.toLowerCase() === filters.status;
    const matchesLocation = !filters.location || device.location.toLowerCase().includes(filters.location.toLowerCase());
    
    return matchesSerialNumber && matchesStatus && matchesLocation;
  });

  // Filter change handlers
  const handleDateChange = (dates: any) => {
    if (dates) {
      const [playTimeStart, playTimeEnd] = dates;
      setFilters(prev => ({
        ...prev,
        playTimeStart: playTimeStart.utc().format("YYYY-MM-DD"),
        playTimeEnd: playTimeEnd.utc().format("YYYY-MM-DD"),
      }));
    } else {
      setFilters(prev => ({
        ...prev,
        playTimeStart: null,
        playTimeEnd: null,
      }));
    }
  };

  const handleResetFilters = () => {
    setFilters({
      serialNumber: null,
      status: null,
      location: null,
      playTimeStart: null,
      playTimeEnd: null
    });
    setResetCounter(prev => prev + 1);
  };

  // Form state for adding/editing devices
  const [deviceForm, setDeviceForm] = useState({
    serialNumber: '',
    customId: '',
    location: 'Warehouse'
  });

  // Handle form changes
  const handleFormChange = (field: string, value: string) => {
    setDeviceForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Handle adding new device
  const handleAddDevice = () => {
    if (!deviceForm.serialNumber.trim()) {
      setAlertMessage('Serial number is required');
      setShowSuccessAlert(true);
      return;
    }

    const newDevice = {
      id: `device-${productId}-${Date.now()}`,
      serialNumber: deviceForm.serialNumber,
      customId: deviceForm.customId,
      status: 'Available',
      location: deviceForm.location,
      lastSeen: ''
    };

    setDevices(prev => [...prev, newDevice]);
    setShowAddDeviceModal(false);
    setDeviceForm({ serialNumber: '', customId: '', location: 'Warehouse' });
    setAlertMessage('Device added successfully!');
    setShowSuccessAlert(true);
  };

  // Handle editing device
  const handleEditDevice = () => {
    if (!deviceForm.serialNumber.trim()) {
      setAlertMessage('Serial number is required');
      setShowSuccessAlert(true);
      return;
    }

    setDevices(prev => prev.map(device => 
      device.id === editingDevice.id 
        ? { ...device, serialNumber: deviceForm.serialNumber, customId: deviceForm.customId, location: deviceForm.location }
        : device
    ));

    setShowEditDeviceModal(false);
    setEditingDevice(null);
    setDeviceForm({ serialNumber: '', customId: '', location: 'Warehouse' });
    setAlertMessage('Device updated successfully!');
    setShowSuccessAlert(true);
  };

  // Handle delete device
  const handleDeleteDevice = (deviceId: string) => {
    setDevices(prev => prev.filter(device => device.id !== deviceId));
    setAlertMessage('Device removed successfully!');
    setShowSuccessAlert(true);
  };

  // Handle edit click
  const handleEditClick = (device: any) => {
    setEditingDevice(device);
    setDeviceForm({
      serialNumber: device.serialNumber,
      customId: device.customId,
      location: device.location
    });
    setShowEditDeviceModal(true);
  };

  if (!product) {
    return (
      <Container fluid>
        <Card className={styles.card}>
          <Card.Body>
            <h2>Product not found</h2>
            <div>Sorry, the product you're looking for doesn't exist.</div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid>
      <Form>
        <Card className={styles.card}>
          <Card.Body>
            <Card.Title style={{ display: 'flex', justifyContent: 'space-between' }}>
              <PageTitle title={`Product Inventory: ${product.name}`} />
              <Button 
                onClick={handleResetFilters}
                style={{ 
                  background: 'white', 
                  color: '#333', 
                  border: '1px solid #e0e0e0', 
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontWeight: '500'
                }}
              >
                <FilterOutlined /> Reset All
              </Button>
            </Card.Title>
          
          {/* Success Alert */}
          {showSuccessAlert && (
            <Alert 
              variant="success" 
              onClose={() => setShowSuccessAlert(false)} 
              dismissible
              className="mb-3"
            >
              {alertMessage}
            </Alert>
          )}
          
          {/* Filters */}
          <Row className="my-2">
            <Col>
              <Row>
                {selectInputs.map((input) => (
                  <Col key={input.name} md={4}>
                    <FormItem
                      name={input.name}
                      label={input.label}
                      className="customSelectWrapper"
                    >
                      <Select
                        key={`${input.name}-reset-${resetCounter}`}
                        showSearch
                        allowClear
                        className="customSelect"
                        placeholder={input.placeholder}
                        value={filters[input.name as keyof typeof filters]}
                        onChange={(value) => setFilters(prev => ({ ...prev, [input.name]: value }))}
                        options={input.options}
                        optionFilterProp="label"
                        style={{ width: "100%" }}
                      />
                    </FormItem>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
          <Row className="my-2">
            <Col
              sm={12}
              lg={6}
              className="d-flex flex-column justify-content-end"
            >
              <FormItem
                name="dateRange"
                validateTrigger="onFinish"
                label="Filter by Date Range"
                className="mb-0"
              >
                <DateRangePicker
                  name="dateRange"
                  maxDate={dayjs()}
                  onChange={handleDateChange}
                />
              </FormItem>
            </Col>
          </Row>
          
          {/* Device Management Section */}
          <div className={styles.deviceSection}>
            <div className={styles.deviceHeader}>
              <h4 className={styles.deviceTitle}>Available Inventory</h4>
              <Button 
                variant="primary" 
                size="sm"
                onClick={() => setShowAddDeviceModal(true)}
                className={styles.addDeviceButton}
              >
                <PlusOutlined /> Add Device
              </Button>
            </div>
            
            <Table hover responsive className={`legacy-row-height-table ${styles.inventoryTable}`}>
              <thead>
                <tr>
                  <th>Serial Number</th>
                  <th>Custom ID</th>
                  <th>Status</th>
                  <th>Location</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredDevices.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="text-center text-muted py-4">
                      No devices found
                    </td>
                  </tr>
                ) : (
                  filteredDevices.map((device) => (
                    <tr key={device.id}>
                      <td className={styles.serialNumber}>{device.serialNumber}</td>
                      <td className={styles.customId}>
                        {device.customId || <span className="text-muted">Not assigned</span>}
                      </td>
                      <td>
                        <span className={`${styles.statusBadge} ${styles[device.status.toLowerCase()]}`}>
                          {device.status}
                        </span>
                      </td>
                      <td>{device.location}</td>
                      <td>
                        <Button 
                          variant="outline-primary" 
                          size="sm"
                          onClick={() => handleEditClick(device)}
                          className={styles.actionButton}
                        >
                          <EditOutlined />
                        </Button>
                        <Button 
                          variant="outline-danger" 
                          size="sm"
                          onClick={() => handleDeleteDevice(device.id)}
                          className={styles.actionButton}
                        >
                          <DeleteOutlined />
                        </Button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </Table>
          </div>
          </Card.Body>
        </Card>
      </Form>

      {/* Add Device Modal */}
      <Modal show={showAddDeviceModal} onHide={() => setShowAddDeviceModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Device</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Serial Number <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={deviceForm.serialNumber}
                onChange={(e) => handleFormChange('serialNumber', e.target.value)}
                placeholder="e.g., KKP8-006-STU901"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Custom ID</Form.Label>
              <Form.Control
                type="text"
                value={deviceForm.customId}
                onChange={(e) => handleFormChange('customId', e.target.value)}
                placeholder="e.g., CLIENT-004-ZONE-D"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                value={deviceForm.location}
                onChange={(e) => handleFormChange('location', e.target.value)}
                placeholder="e.g., Warehouse, New York, NY"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowAddDeviceModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleAddDevice}>
            Add Device
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Edit Device Modal */}
      <Modal show={showEditDeviceModal} onHide={() => setShowEditDeviceModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Device</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Serial Number <span className="text-danger">*</span></Form.Label>
              <Form.Control
                type="text"
                value={deviceForm.serialNumber}
                onChange={(e) => handleFormChange('serialNumber', e.target.value)}
                placeholder="e.g., KKP8-006-STU901"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Custom ID</Form.Label>
              <Form.Control
                type="text"
                value={deviceForm.customId}
                onChange={(e) => handleFormChange('customId', e.target.value)}
                placeholder="e.g., CLIENT-004-ZONE-D"
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Location</Form.Label>
              <Form.Control
                type="text"
                value={deviceForm.location}
                onChange={(e) => handleFormChange('location', e.target.value)}
                placeholder="e.g., Warehouse, New York, NY"
              />
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEditDeviceModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleEditDevice}>
            Save Changes
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default ProductInventory;
